import type { OrderDto } from '@tikkas/shared';
import { api, ApiError } from './api.js';

/*
 * Guest checkout (CLAUDE.md §4, Saif 24 Oct 2026; docs/30): an order placed without an account lives on this phone as a
 * reference (its id and the link token the server gave), so Home's live row, the status page and the pickup code work for a
 * guest too. Joining attaches the order to the account and the reference is forgotten (the token stops working then).
 */

export interface GuestOrderRef {
  id: string;
  token: string;
  number: number;
  readyAt: string;
  /** What the guest typed: for the ticket, and to prefill the join page. The phone is only ever on this device and the server. */
  name: string;
  phone: string;
  at: string;
}

export interface GuestStatus { order: OrderDto; nudge: { points: number; welcomePoints: number } | null }

export const guestOrderPath = (id: string, token: string, extra = ''): string => `/order/${id}?t=${token}${extra}`;
export const guestApi = (id: string, token: string): string => `/api/guest/orders/${id}?t=${token}`;

/** The order by its link; 'gone' once it has joined an account (410) or no longer exists (404). */
export async function fetchGuestOrder(id: string, token: string): Promise<GuestStatus | 'gone'> {
  try {
    return await api<GuestStatus>(guestApi(id, token));
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 410)) return 'gone';
    throw e;
  }
}

/** The join page's `guest=` value and its reading. */
export const guestParam = (id: string, token: string): string => `${id}.${token}`;
export function parseGuestParam(v: string | null): { id: string; token: string } | null {
  if (!v) return null;
  const i = v.lastIndexOf('.');
  if (i <= 0) return null;
  const id = v.slice(0, i), token = v.slice(i + 1);
  return /^[0-9a-f]{32}$/.test(token) && id ? { id, token } : null;
}

/** The join link from a guest order's nudge: back to the order afterwards, the name and mobile prefilled, the order attached on sign-in. */
export function joinFromGuestPath(ref: { id: string; token: string; name: string; phone: string }): string {
  const q = new URLSearchParams({ next: `/order/${ref.id}`, guest: guestParam(ref.id, ref.token) });
  if (ref.name) q.set('name', ref.name);
  if (ref.phone) q.set('phone', ref.phone.replace(/^\+44/, ''));
  return `/join?${q.toString()}`;
}

/** Orders a guest can still act on: live, or collected today. Anything older drops off the phone. */
export function guestRefLive(ref: GuestOrderRef, now: Date = new Date()): boolean {
  return now.getTime() - new Date(ref.at).getTime() < 2 * 24 * 3600_000;
}
